import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { APIURL } from "../config";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Importing eye icons
import './registerpage.css'
import Snackbar from './Snackbar';

function RegisterPage() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);  // State to toggle password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "info" });
  const toggleShowPassword = () => setShowPassword(!showPassword);
  const toggleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);
  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const handleRegistration = async () => {
    // Reset previous errors
    setFirstNameError("");
    setLastNameError("");
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setError("");

    // Basic Validations
    let isValid = true;  // Track validation status

    if (!firstName) {
      setFirstNameError("First Name is required.");
      isValid = false;
    }

    if (!lastName) {
      setLastNameError("Last Name is required.");
      isValid = false;
    }

    if (!email) {
      setEmailError("Email is required.");
      isValid = false;
    } else {
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setEmailError("Invalid email format.");
        isValid = false;
      }
    }

    // Trim leading/trailing whitespace (common copy-paste artifact from password managers)
    const trimmedPassword = password ? password.trim() : '';
    if (trimmedPassword !== password) {
      setPassword(trimmedPassword); // update the field so user sees the trimmed value
    }

    if (!trimmedPassword) {
      setPasswordError("Password is required.");
      isValid = false;
    } else if (/[^\x20-\x7E]/.test(trimmedPassword)) {
      // Reject non-ASCII characters (é, ñ, etc.) — bcrypt has a 72-byte limit and multi-byte chars cause inconsistent hashing
      setPasswordError("Password can only contain English letters, numbers, spaces, and standard special characters.");
      isValid = false;
    } else {
      // Password Strength Validation (must match server-side policy: min 10 chars, lowercase, uppercase, digit, special)
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{10,}$/;

      if (!passwordRegex.test(trimmedPassword)) {
        setPasswordError("Password does not meet the requirements below.");
        isValid = false;
      }
    }

    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your password.");
      isValid = false;
    } else if (trimmedPassword !== confirmPassword.trim()) {
      setConfirmPasswordError("Passwords do not match.");
      isValid = false;
    }
    // Stop execution if any validation fails
    if (!isValid) {
      setError("Please fill in all fields correctly.");
      return;
    }
    // Proceed with API call only if validation passes
    setIsLoading(true);
    try {
      const response = await fetch(`${APIURL}/createaccount`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ firstName, lastName, email, password }),
      });

      const data = await response.json();
      if (data.message === "Account created Successfully!") {
        setSnackbar({ open: true, message: "Account created successfully! Redirecting to login...", type: "success" });
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setSnackbar({ open: true, message: data.message || "An error occurred. Please try again.", type: "error" });
        setError(data.message || "An error occurred. Please try again.");
      }
    } catch (error) {
      setSnackbar({ open: true, message: "An error occurred while creating an account.", type: "error" });
      setError("An error occurred while creating an account.");
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="loginsignup">
      <Snackbar
        message={snackbar.message}
        type={snackbar.type}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
      <div className="loginsignup-container">
        <h1>Create Account</h1>
        <p style={{ margin: '0 0 4px 0', color: '#718096', fontSize: '15px' }}>Sign up to get started</p>
          <div className="loginsignup-fields">
            <label>
              First Name
              <input
                type="text"
                className="firstName"
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              {firstNameError && <span className="field-error">{firstNameError}</span>}
            </label>

            <label>
              Last Name
              <input
                type="text"
                className="lastName"
                placeholder="Enter your last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              {lastNameError && <span className="field-error">{lastNameError}</span>}
            </label>

            <label>
              Email Address
              <input
                type="email"
                className="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailError && <span className="field-error">{emailError}</span>}
            </label>

            <label>
              Password
              <div className="password-container">
                <input
                  type={showPassword ? "text" : "password"}
                  className="password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                />
                <span className="eye-button" onClick={toggleShowPassword}>
                  {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </span>
              </div>
              {passwordError && <span className="field-error">{passwordError}</span>}

              {/* Password Policy Requirements */}
              <div className="password-policy">
                <p className="password-policy-title">Password must contain:</p>
                <ul className="password-policy-list">
                  <li className={!/[^\x20-\x7E]/.test(password) ? 'met' : 'unmet'}>
                    {!/[^\x20-\x7E]/.test(password) ? '\u2713' : '\u2717'} Only English letters, numbers, and standard symbols
                  </li>
                  <li className={password.trim().length >= 10 ? 'met' : 'unmet'}>
                    {password.trim().length >= 10 ? '\u2713' : '\u2717'} At least 10 characters
                  </li>
                  <li className={/[a-z]/.test(password) ? 'met' : 'unmet'}>
                    {/[a-z]/.test(password) ? '\u2713' : '\u2717'} One lowercase letter (a-z)
                  </li>
                  <li className={/[A-Z]/.test(password) ? 'met' : 'unmet'}>
                    {/[A-Z]/.test(password) ? '\u2713' : '\u2717'} One uppercase letter (A-Z)
                  </li>
                  <li className={/\d/.test(password) ? 'met' : 'unmet'}>
                    {/\d/.test(password) ? '\u2713' : '\u2717'} One numeric digit (0-9)
                  </li>
                  <li className={/[@$!%*?&]/.test(password) ? 'met' : 'unmet'}>
                    {/[@$!%*?&]/.test(password) ? '\u2713' : '\u2717'} One special character (@$!%*?&)
                  </li>
                </ul>
              </div>
            </label>

            <label>
              Confirm Password
              <div className="password-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="password-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                />
                <span className="eye-button" onClick={toggleShowConfirmPassword}>
                  {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </span>
              </div>
              {confirmPasswordError && <span className="field-error">{confirmPasswordError}</span>}
            </label>
          </div>

          <button className={`submit-button ${isLoading ? "btn-loading" : ""}`} onClick={handleRegistration} disabled={isLoading}>
            {isLoading && <span className="btn-spinner"></span>}
            {isLoading ? "Creating Account..." : "Create Account"}
          </button>

          {error && <div className="form-error-alert">{error}</div>}

          <p style={{ marginTop: '10px', textAlign: 'center' }}>
            <span
              className="register"
              role="link"
              tabIndex={0}
              onClick={() => navigate("/login")}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate("/login"); }}
            >
              Already have an account? Log in
            </span>
          </p>

        </div>
        <p className="register-data">
          Petivity Analysis Kits are not for diagnostic purposes. Information provided by these kits is not intended to diagnose, treat, cure, or prevent any disease. Information is intended for optimizing pet health & wellbeing. We recommend regular wellness check-ups with your Veterinarian. If you want to address a symptom, we recommend consulting with your Veterinarian.
        </p>
      </div>
  );
}

export default RegisterPage;
